import useApi from "@/hooks/useApi";
import useSkillListItems from "@/hooks/useSkillListItems";
import React from "react";
import CreateSkillModal from "./CreateSkillModal";
import { SkillListItem } from "@/providers/SkillsProvider";
import EditSkillModal from "./EditSkillModal";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import { IconTrash } from "@velaro/velaro-component-lib";
import Tooltip from "@/components/Tooltip";

export default function Skills() {
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [editSkillId, setEditSkillId] = React.useState<number>();
  const api = useApi();
  const { skills, setSkills } = useSkillListItems();

  async function deleteSkill(id: number) {
    if (
      confirm(
        "Are you sure you want to delete this skill? Deleting this skill could cause users to no longer have this skill."
      )
    ) {
      await api.messaging.delete(`Skills/${id}`);
      setSkills(skills.filter((x) => x.id !== id));
    }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div />
        <PrimaryBlueButton
          onClick={() => setShowCreateModal(true)}
          label="New Skill"
        />
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Name</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">ID</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Auto Routing</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Routing Type</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Auto Unassignment</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Timeout</th>
              <th className="px-4 py-3" style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill, i) => (
              <tr key={skill.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i === skills.length - 1 ? "border-b-0" : ""}`}>
                <td className="px-4 py-3 text-sm font-medium text-slate-800">{skill.name}</td>
                <td className="px-4 py-3 text-sm text-slate-500">{skill.id}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{skill.autoRoute ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{skill.routingType}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{skill.autoUnassign ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{skill.unassignTime}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 text-slate-400 justify-end">
                    <Tooltip text="Edit" direction="top" minWidth="50px">
                      <div
                        className="hover:cursor-pointer hover:text-primary transition-colors"
                        onClick={() => setEditSkillId(skill.id)}
                      >
                        <IconEditPencil2 />
                      </div>
                    </Tooltip>
                    <Tooltip text="Delete" direction="top" minWidth="60px">
                      <div
                        className="hover:cursor-pointer hover:text-red-500 transition-colors"
                        onClick={() => deleteSkill(skill.id)}
                      >
                        <IconTrash />
                      </div>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
            {skills.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-400">
                  No skills yet. Click <span className="font-medium">New Skill</span> to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showCreateModal && (
        <CreateSkillModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSkillCreated={(skill: SkillListItem) => {
            setShowCreateModal(false);
            setSkills([...skills, skill]);
          }}
        />
      )}
      {editSkillId && (
        <EditSkillModal
          skillId={editSkillId}
          onClose={() => setEditSkillId(undefined)}
          onSkillUpdated={(skill: SkillListItem) => {
            setEditSkillId(undefined);
            setSkills(skills.map((x) => (x.id === skill.id ? skill : x)));
          }}
        />
      )}
    </>
  );
}
